<template>
  <div
    class="opportunity-summary"
    :class="{ 'opportunity-summary--card': isCard }"
  >
    <div v-if="isLoading" class="opportunity-summary__loading">
      <spinner size="small" color-scheme="dark" />
    </div>
    <p
      v-else-if="hasError"
      class="opportunity-summary__message opportunity-summary__message--error"
    >
      {{ $t('CONTACT_PANEL.OPPORTUNITIES.LOAD_ERROR') }}
    </p>
    <p v-else-if="!opportunities.length" class="opportunity-summary__message">
      {{ emptyMessage }}
    </p>
    <ul v-else class="opportunity-summary__list">
      <li
        v-for="opportunity in opportunities"
        :key="opportunity.id"
        :class="isCard ? 'opportunity-card' : 'opportunity-summary__item'"
      >
        <router-link
          v-if="isCard"
          :to="opportunityRoute(opportunity)"
          class="opportunity-card__link"
        >
          <div class="opportunity-card__header">
            <span class="opportunity-card__id">#{{ opportunity.id }}</span>
            <span
              class="opportunity-card__status"
              :class="`opportunity-card__status--${opportunity.status}`"
            >
              {{ statusLabel(opportunity.status) }}
            </span>
          </div>
          <p class="opportunity-card__name" :title="opportunity.name">
            {{ opportunity.name }}
          </p>
          <p
            v-if="opportunity.description"
            class="opportunity-card__description"
            :title="opportunity.description"
          >
            {{ opportunity.description }}
          </p>
          <dl class="opportunity-card__details">
            <dt>{{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.PIPELINE') }}</dt>
            <dd>{{ pipelineName(opportunity) }}</dd>
            <dt>{{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.STAGE') }}</dt>
            <dd>
              <span
                class="opportunity-summary__stage-dot"
                :style="{ backgroundColor: stageColor(opportunity) }"
              />
              {{ stageName(opportunity) }}
            </dd>
            <dt>{{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.VALUE') }}</dt>
            <dd class="opportunity-card__value">
              {{ formatCurrencyAmount(opportunity) }}
            </dd>
            <dt>{{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.OWNER') }}</dt>
            <dd>{{ ownerName(opportunity) }}</dd>
          </dl>
        </router-link>
        <template v-else>
          <router-link
            :to="opportunityRoute(opportunity)"
            class="opportunity-summary__id"
          >
            #{{ opportunity.id }}
          </router-link>
          <p class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.NAME') }}:
            </strong>
            <span
              class="opportunity-summary__stage-dot"
              :style="{ backgroundColor: stageColor(opportunity) }"
            />
            {{ opportunity.name }}
          </p>
          <p v-if="opportunity.description" class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.DESCRIPTION') }}:
            </strong>
            {{ opportunity.description }}
          </p>
          <p class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.PIPELINE') }}:
            </strong>
            {{ pipelineName(opportunity) }}
          </p>
          <p class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.STAGE') }}:
            </strong>
            {{ stageName(opportunity) }}
          </p>
          <p class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.STATUS') }}:
            </strong>
            <span
              class="opportunity-summary__status"
              :class="`opportunity-summary__status--${opportunity.status}`"
            >
              {{ statusLabel(opportunity.status) }}
            </span>
          </p>
          <p class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.VALUE') }}:
            </strong>
            {{ formatCurrencyAmount(opportunity) }}
          </p>
          <p class="opportunity-summary__field">
            <strong>
              {{ $t('CONTACT_PANEL.OPPORTUNITIES.FIELDS.OWNER') }}:
            </strong>
            {{ ownerName(opportunity) }}
          </p>
        </template>
      </li>
    </ul>
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner';

export default {
  components: {
    Spinner,
  },
  props: {
    opportunities: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      required: true,
    },
    variant: {
      type: String,
      default: 'compact',
      validator: value => ['compact', 'card'].includes(value),
    },
  },
  computed: {
    isCard() {
      return this.variant === 'card';
    },
  },
  methods: {
    opportunityRoute(opportunity) {
      return {
        name: 'opportunities_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          opportunityId: opportunity.id,
        },
      };
    },
    pipelineName(opportunity) {
      return opportunity.pipeline ? opportunity.pipeline.name : '-';
    },
    stageName(opportunity) {
      return opportunity.pipeline_stage ? opportunity.pipeline_stage.name : '-';
    },
    stageColor(opportunity) {
      return opportunity.pipeline_stage?.color || 'var(--color-border)';
    },
    ownerName(opportunity) {
      return opportunity.owner ? opportunity.owner.name : '-';
    },
    statusLabel(status) {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.STATUS_VALUES.${status}`
      );
    },
    formatCurrencyAmount(opportunity) {
      const amount = Number(opportunity.value || 0);
      return `${opportunity.currency || 'BRL'} ${amount.toLocaleString(
        'pt-BR',
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.opportunity-summary__loading {
  display: flex;
  justify-content: center;
  padding: var(--space-small) 0;
}

.opportunity-summary__message {
  color: var(--b-500);
  margin-bottom: var(--space-normal);

  &--error {
    color: var(--r-500);
  }
}

.opportunity-summary__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.opportunity-summary__item {
  border-bottom: 1px solid var(--color-border);
  padding: var(--space-small) 0;

  &:last-child {
    border-bottom: none;
  }
}

.opportunity-summary__id {
  color: var(--w-500);
  display: block;
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-smaller);
}

.opportunity-summary__field {
  margin: 0 0 var(--space-micro);
  word-break: break-word;
}

.opportunity-summary__stage-dot {
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  margin-right: var(--space-smaller);
  width: 8px;
}

.opportunity-summary__status {
  font-weight: var(--font-weight-medium);

  &--open {
    color: var(--y-700);
  }

  &--won {
    color: var(--g-700);
  }

  &--lost,
  &--canceled {
    color: var(--r-700);
  }
}

.opportunity-summary--card {
  .opportunity-summary__loading {
    padding: var(--space-large) 0;
  }

  .opportunity-summary__message {
    color: var(--s-600);
    margin: 0;
    padding: var(--space-large) var(--space-normal);
    text-align: center;
  }

  .opportunity-summary__message--error {
    color: var(--r-500);
  }

  .opportunity-summary__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-small);
  }
}

.opportunity-card__link {
  background: var(--white);
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-small);
  color: var(--s-800);
  display: block;
  padding: var(--space-normal);
  transition: box-shadow 0.15s ease-in-out;

  &:hover,
  &:focus {
    box-shadow: var(--shadow-medium);
    color: var(--s-800);
  }
}

.opportunity-card__header {
  align-items: center;
  display: flex;
  gap: var(--space-small);
  justify-content: space-between;
}

.opportunity-card__id {
  color: var(--w-500);
  font-weight: var(--font-weight-bold);
}

.opportunity-card__status {
  border-radius: var(--border-radius-normal);
  flex-shrink: 0;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  padding: 0 var(--space-smaller);

  &--open {
    background: var(--y-100);
    color: var(--y-700);
  }

  &--won {
    background: var(--g-100);
    color: var(--g-700);
  }

  &--lost,
  &--canceled {
    background: var(--r-100);
    color: var(--r-700);
  }
}

.opportunity-card__name,
.opportunity-card__description {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.opportunity-card__name {
  color: var(--s-900);
  font-weight: var(--font-weight-medium);
  margin-top: var(--space-small);
}

.opportunity-card__description {
  color: var(--b-600);
  margin-top: var(--space-micro);
}

.opportunity-card__details {
  align-items: baseline;
  border-top: 1px solid var(--s-50);
  column-gap: var(--space-normal);
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  margin: var(--space-slab) 0 0;
  padding-top: var(--space-slab);
  row-gap: var(--space-smaller);

  dt {
    color: var(--b-600);
    font-size: var(--font-size-mini);
    font-weight: var(--font-weight-normal);
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
}

.opportunity-card__value {
  font-weight: var(--font-weight-medium);
}
</style>
